
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Card, CardContent } from "@/components/ui/card";
import { useForm } from "react-hook-form";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

// Form validation schema
const formSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  question: z.string().min(10, "Your question must be at least 10 characters")
});

const BeginnerQuestionForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form setup
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      question: ""
    }
  });

  const onSubmit = async (data: { email: string; question: string }) => {
    console.log("Form submitted:", data);
    setIsSubmitting(true);
    
    try {
      // Send data to the same webhook used in the Contact page
      const response = await fetch('https://hook.integrator.boost.space/gulmb6bssd6qbm8x8hrmu9url24teykw', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: "Question from Beginners Page",
          email: data.email,
          company: "",
          message: data.question,
          notificationEmail: "george@upfixe.com" // Added notification email
        }),
      });
      
      if (response.ok) {
        toast.success("Question submitted! We'll email you shortly.");
        form.reset();
      } else {
        toast.error("There was an error sending your message. Please try again later.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("There was an error sending your message. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 px-4 bg-gradient-to-b from-white to-blue-50 border-t border-blue-100">
      <div className="container mx-auto max-w-3xl">
        <div className="text-center mb-12">
          <Badge className="px-3 py-1 text-sm bg-blue-600 text-white border-blue-700 mb-4">
            Still Have Questions?
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ask us anything about Google Ads
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Our Google Ads experts will email you back with a helpful answer, even if you're not a customer yet.
          </p>
        </div>
        
        <Card>
          <CardContent className="p-8">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email address</FormLabel>
                      <FormControl>
                        <Input placeholder="your@email.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="question"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Your question about Google Ads</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="What would you like to know about creating Google Ads as a beginner?" 
                          className="min-h-32"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <Button 
                  type="submit" 
                  className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Submit Question"}
                </Button>
              </form>
            </Form>
            
            <p className="text-sm text-muted-foreground text-center mt-4">
              We'll email you a response within 24 hours. No spam, we promise!
            </p>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default BeginnerQuestionForm;
